// FreeTrialManager.js - Fixed to prevent forced recreation after expiration
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { encryptData, decryptData } from 'src/encryptionUtils';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, push, get, query, orderByChild, equalTo } from 'firebase/database';

// Check if we're in incognito mode and set a flag
let isIncognitoMode = false;
try {
  const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
  if (fs) {
    fs(window.TEMPORARY, 100, function() {
      isIncognitoMode = false;
    }, function() {
      isIncognitoMode = true;
      console.log("FreeTrialManager: Detected incognito mode");
    });
  }
} catch (e) {
  console.log("FreeTrialManager: Error detecting incognito mode:", e);
}

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAF_vfXQtqMyPITZc7vGRtT2-IdIPbZ8qE",
  authDomain: "blue-comet-free-trial.firebaseapp.com",
  databaseURL: "https://blue-comet-free-trial-default-rtdb.firebaseio.com",
  projectId: "blue-comet-free-trial",
  storageBucket: "blue-comet-free-trial.firebasestorage.app",
  messagingSenderId: "421726724578",
  appId: "1:421726724578:web:ef2a2ed2378053d4fe59bb",
  measurementId: "G-TS4M3HFV2Z"
};

// Initialize Firebase
let app;
let database;

try {
  app = initializeApp(firebaseConfig);
  database = getDatabase(app);
  console.log("FreeTrialManager: Firebase initialized successfully");
} catch (error) {
  console.error("FreeTrialManager: Firebase initialization error:", error);
}

// ----- Configuration -----
const INITIAL_PROMPT_COUNT = 15; // Set to 3 for testing
const WARNING_THRESHOLD = 0.2; // Show warning when 20% of prompts remain
const TRIAL_API_KEYS = [
  { id: 'keyA', key: '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' },
  { id: 'keyB', key: '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' },
  { id: 'keyC', key: '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' }
];

// Global free trial status flag - set this to false to disable free trials globally
const FREE_TRIAL_ENABLED = true;

// CRITICAL: Keys for tracking trial state
const FINAL_PROMPT_USED_KEY = 'freeTrialFinalPromptUsed';
const TRIAL_EXPIRED_KEY = 'freeTrialExpired';
const TRIAL_INIT_LOCK_KEY = 'freeTrialInitLock';
const TRIAL_INIT_TIMESTAMP_KEY = 'freeTrialInitTimestamp';
const FINGERPRINT_HISTORY_KEY = 'freeTrialFingerprintHistory';

// Encrypt keys at initialization
const ENCRYPTED_TRIAL_API_KEYS = TRIAL_API_KEYS.map(item => ({
  ...item,
  key: encryptData(item.key)
}));

// Internal trial state
let trialData = null;

// Track whether warning has been shown
let hasShownWarning = false;

// Keep track of initialization attempts
let initializationAttempts = 0;
const MAX_INIT_ATTEMPTS = 3;

// NEW: Track if we're in a settings dialog context
let inSettingsDialog = false;

// NEW: Track if this is a legitimate test/debug environment
let isTestEnvironment = false;
try {
  isTestEnvironment = process.env.NODE_ENV === 'development' || 
                       process.env.NODE_ENV === 'test' ||
                       window.location.hostname === 'localhost' ||
                       window.location.hostname === '127.0.0.1';
} catch (e) {
  isTestEnvironment = false;
}

// NEW: Function to safely check if trial is expired
const isTrialExpired = () => {
  // Check the dedicated expired flag
  if (localStorage.getItem(TRIAL_EXPIRED_KEY) === 'true') {
    return true;
  }
  
  // Check if final prompt was used and remaining is 0 or 1
  const finalPromptUsed = localStorage.getItem(FINAL_PROMPT_USED_KEY) === 'true';
  try {
    const storedTrial = localStorage.getItem('freeTrialData');
    if (storedTrial) {
      const parsedData = JSON.parse(storedTrial);
      if (!parsedData.active || parsedData.remaining <= 0 || (finalPromptUsed && parsedData.remaining <= 1)) {
        return true;
      }
    }
  } catch (err) {
    console.error("Error checking trial expiration status:", err);
  }
  
  return false;
};

// NEW: Function to set expired state consistently
const markTrialAsExpired = () => {
  console.log("FreeTrialManager: Marking trial as permanently expired");
  localStorage.setItem(TRIAL_EXPIRED_KEY, 'true');
  
  // Also update the trial data if available
  try {
    const storedTrial = localStorage.getItem('freeTrialData');
    if (storedTrial) {
      const parsedData = JSON.parse(storedTrial);
      parsedData.active = false;
      parsedData.remaining = 0;
      parsedData.expiredAt = parsedData.expiredAt || new Date().toISOString();
      localStorage.setItem('freeTrialData', JSON.stringify(parsedData));
    }
  } catch (err) {
    console.error("Error updating trial data while marking as expired:", err);
  }
};

// NEW: Track fingerprint history persistently to prevent trial abuse
const addToFingerprintHistory = (fingerprint) => {
  try {
    // Get existing history
    const historyString = localStorage.getItem(FINGERPRINT_HISTORY_KEY);
    let history = [];
    
    if (historyString) {
      history = JSON.parse(historyString);
    }
    
    // Add new fingerprint if not already present
    if (!history.includes(fingerprint)) {
      history.push(fingerprint);
      localStorage.setItem(FINGERPRINT_HISTORY_KEY, JSON.stringify(history));
    }
  } catch (err) {
    console.error("Failed to update fingerprint history:", err);
  }
};

const hasUsedTrialBefore = (fingerprint) => {
  try {
    // Get existing history
    const historyString = localStorage.getItem(FINGERPRINT_HISTORY_KEY);
    
    if (historyString) {
      const history = JSON.parse(historyString);
      return history.includes(fingerprint);
    }
  } catch (err) {
    console.error("Failed to check fingerprint history:", err);
  }
  
  return false;
};

// NEW: Initialization prevention mechanism
const createInitLock = () => {
  const now = Date.now();
  localStorage.setItem(TRIAL_INIT_LOCK_KEY, 'true');
  localStorage.setItem(TRIAL_INIT_TIMESTAMP_KEY, now.toString());
  return now;
};

const clearInitLock = () => {
  localStorage.removeItem(TRIAL_INIT_LOCK_KEY);
  localStorage.removeItem(TRIAL_INIT_TIMESTAMP_KEY);
};

const hasValidInitLock = () => {
  if (localStorage.getItem(TRIAL_INIT_LOCK_KEY) !== 'true') {
    return false;
  }
  
  // Check if the lock is too old (over 30 seconds)
  try {
    const timestamp = Number(localStorage.getItem(TRIAL_INIT_TIMESTAMP_KEY));
    const now = Date.now();
    return now - timestamp < 30000; // 30 seconds
  } catch {
    return false;
  }
};

const FreeTrialManager = {
  // NEW: Signal we're entering settings dialog
  enterSettingsDialog() {
    console.log("FreeTrialManager: Entering settings dialog");
    inSettingsDialog = true;
  },
  
  // Signal we're exiting settings dialog with EXPLICIT thread content preservation
  exitSettingsDialog() {
    console.log("FreeTrialManager: Exiting settings dialog");
    inSettingsDialog = false;
    
    // First, dispatch a dedicated event specifically for settings exit that guarantees thread preservation
    // This should be handled separately in the UI code with highest priority
    console.log("FreeTrialManager: Dispatching explicit settings exit event");
    const settingsExitEvent = new CustomEvent('trialSettingsExit', { 
      detail: { 
        preserveThreadContent: true,
        timestamp: Date.now(),
        exitingSettings: true
      } 
    });
    window.dispatchEvent(settingsExitEvent);
    
    // CRITICAL FIX: If trial was expired before entering settings,
    // make sure it stays expired after exiting
    if (localStorage.getItem(TRIAL_EXPIRED_KEY) === 'true') {
      console.log("FreeTrialManager: Trial was already expired, ensuring it stays expired after settings");
      this.ensureExpiredStateConsistency(true); // Pass flag to force thread content preservation
    } else {
      // Still dispatch the regular event for other UI updates
      console.log("FreeTrialManager: Dispatching regular update event with thread preservation");
      const event = new CustomEvent('trialUpdated', { 
        detail: { 
          preserveThreadContent: true,
          fromSettingsExit: true
        } 
      });
      window.dispatchEvent(event);
    }
    
    // Add a small delay and dispatch one more preservation event to catch any race conditions
    setTimeout(() => {
      console.log("FreeTrialManager: Dispatching final thread preservation event");
      const finalEvent = new CustomEvent('preserveTrialThreadContent', { 
        detail: { timestamp: Date.now() } 
      });
      window.dispatchEvent(finalEvent);
    }, 100);
  },
  
  // Check and enforce expired state consistency with optional force preserve flag
  ensureExpiredStateConsistency(forcePreserveContent = false) {
    if (isTrialExpired()) {
      markTrialAsExpired();
      // Update the in-memory trial data too
      if (trialData) {
        trialData.active = false;
        trialData.remaining = 0;
      }
      
      // ADDED: Dispatch event with preserve flag instead of default event
      const event = new CustomEvent('trialUpdated', { 
        detail: { 
          preserveThreadContent: true,
          forcePreserveContent: forcePreserveContent
        } 
      });
      window.dispatchEvent(event);
    }
  },
  
  // CRITICAL FIX: Modified to prevent forced recreation after trial expiration
  async initTrial(forceNew = false) {
    console.log("FreeTrialManager: Starting trial initialization, incognito mode:", isIncognitoMode, "force new:", forceNew);
    
    // CRITICAL FIX: Prevent multiple simultaneous initializations
    if (hasValidInitLock() && !forceNew) {
      console.log("FreeTrialManager: Initialization already in progress, skipping");
      return false;
    }
    
    // CRITICAL FIX: Prevent forced recreation when trial has been used.
    // This is the key fix for the app repeatedly creating new trials
    if (forceNew && !isTestEnvironment) {
      // Check if trial is already expired
      if (localStorage.getItem(TRIAL_EXPIRED_KEY) === 'true') {
        console.log("FreeTrialManager: Cannot force new trial - already used and expired");
        return false;
      }
      
      // Check if final prompt was used
      if (localStorage.getItem(FINAL_PROMPT_USED_KEY) === 'true') {
        console.log("FreeTrialManager: Cannot force new trial - final prompt was already used");
        return false;
      }
    }
    
    // Create initialization lock
    createInitLock();
    
    // Track initialization attempts
    initializationAttempts++;
    
    // Reset warning flag on initialization
    hasShownWarning = false;
    
    // Check if free trials are globally disabled
    if (!FREE_TRIAL_ENABLED) {
      console.log("FreeTrialManager: Free trials are globally disabled");
      clearInitLock();
      return false;
    }
    
    // CRITICAL FIX: Check if trial is already expired
    if (!forceNew && isTrialExpired()) {
      console.log("FreeTrialManager: Trial already expired, skipping initialization");
      // Ensure state consistency
      markTrialAsExpired();
      clearInitLock();
      return false;
    }

    try {
      // If forcing new trial, skip loading from localStorage
      if (!forceNew) {
        // CRITICAL: Check if the final prompt was already used
        const finalPromptUsed = localStorage.getItem(FINAL_PROMPT_USED_KEY) === 'true';
        
        // Load trial state from localStorage if available
        const storedTrial = localStorage.getItem('freeTrialData');
        if (storedTrial) {
          console.log("FreeTrialManager: Found stored trial data");
          try {
            trialData = JSON.parse(storedTrial);
            console.log("FreeTrialManager: Trial active status:", trialData.active);
            
            // CRITICAL FIX: If final prompt was used and remaining is 1, 
            // set remaining to 0 and mark as inactive
            if (finalPromptUsed && trialData.remaining === 1) {
              console.log("FreeTrialManager: Final prompt was previously used, updating state to zero remaining");
              trialData.remaining = 0;
              trialData.active = false;
              trialData.expiredAt = trialData.expiredAt || new Date().toISOString();
              localStorage.setItem('freeTrialData', JSON.stringify(trialData));
              // Also mark as fully expired
              markTrialAsExpired();
              
              // ADDED: Dispatch event with preserve flag
              const event = new CustomEvent('trialUpdated', { 
                detail: { 
                  preserveThreadContent: true
                } 
              });
              window.dispatchEvent(event);
              
              clearInitLock();
              return false;
            }
            
            // FIX: If trial data exists but is marked as inactive and still has remaining prompts,
            // reactivate it. This handles cases where trial was incorrectly deactivated.
            // But ONLY do this if trial isn't explicitly marked as expired
            if (!trialData.active && trialData.remaining > 0 && !finalPromptUsed && 
                localStorage.getItem(TRIAL_EXPIRED_KEY) !== 'true') {
              console.log("FreeTrialManager: Reactivating trial with", trialData.remaining, "prompts remaining");
              trialData.active = true;
              localStorage.setItem('freeTrialData', JSON.stringify(trialData));
            }
            
            // If trial is valid and active, return immediately
            if (trialData.active && trialData.remaining > 0) {
              // ADDED: Dispatch event with preserve flag
              const event = new CustomEvent('trialUpdated', { 
                detail: { 
                  preserveThreadContent: true
                } 
              });
              window.dispatchEvent(event);
              
              // Check if we should show a resource warning
              this.checkResourceWarning();
              
              clearInitLock();
              return true;
            } else if (!trialData.active && trialData.error) {
              console.log("FreeTrialManager: Trial is inactive due to error:", trialData.error);
              
              // If last attempt, and there's a specific error, respect it
              if (initializationAttempts >= MAX_INIT_ATTEMPTS) {
                clearInitLock();
                return false;
              }
              
              // Otherwise, continue with fresh initialization
              console.log("FreeTrialManager: Retrying with fresh initialization");
            }
          } catch (e) {
            console.error("FreeTrialManager: Error parsing stored trial data:", e);
            // Continue with fresh initialization
          }
        }
      } else {
        console.log("FreeTrialManager: Forcing new trial creation");
        // Reset the expired state flags when forcing a new trial
        // But ONLY do this in a test environment or if explicitly allowed
        if (isTestEnvironment) {
          localStorage.removeItem(TRIAL_EXPIRED_KEY);
          localStorage.removeItem(FINAL_PROMPT_USED_KEY);
        }
      }

      // Initialize new trial
      console.log("FreeTrialManager: Initializing new trial");
      
      // 1. Fingerprint Generation with fallback for incognito
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        trialData = { fingerprint: result.visitorId };
        
        // NEW: Check fingerprint history locally too
        if (hasUsedTrialBefore(result.visitorId)) {
          console.log("FreeTrialManager: Fingerprint already used a trial according to local history");
          
          // Only strictly enforce this if forcing a new trial
          if (forceNew && !isTestEnvironment) {
            console.log("FreeTrialManager: Cannot force new trial - fingerprint in history");
            clearInitLock();
            return false;
          }
        }
        
        // Add additional browser data for enhanced fingerprinting
        trialData.deviceData = {
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screenSize: `${window.screen.width}x${window.screen.height}`,
          colorDepth: window.screen.colorDepth,
          platform: navigator.platform,
          doNotTrack: navigator.doNotTrack,
          cookiesEnabled: navigator.cookieEnabled,
          localStorage: !!window.localStorage,
          sessionStorage: !!window.sessionStorage,
          isIncognito: isIncognitoMode
        };
        
        console.log("FreeTrialManager: Generated fingerprint:", trialData.fingerprint);
      } catch (err) {
        console.error("FreeTrialManager: Fingerprint generation failed:", err);
        // Generate a random fingerprint as fallback
        const randomFingerprint = 'temp-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15);
        trialData = { 
          fingerprint: randomFingerprint,
          deviceData: {
            userAgent: navigator.userAgent,
            isIncognito: isIncognitoMode,
            timestamp: Date.now()
          }
        };
        console.log("FreeTrialManager: Using fallback fingerprint:", randomFingerprint);
      }

      // If in incognito mode, prefer to create a local-only trial
      if (isIncognitoMode) {
        console.log("FreeTrialManager: Creating incognito mode trial (local only)");
        localStorage.setItem('freeTrialVerified', 'true');
        trialData.verified = true;
        trialData.isIncognitoTrial = true;
        
        // Assign a trial API key
        const randomIndex = Math.floor(Math.random() * ENCRYPTED_TRIAL_API_KEYS.length);
        trialData.trialKey = ENCRYPTED_TRIAL_API_KEYS[randomIndex];
        trialData.remaining = INITIAL_PROMPT_COUNT;
        trialData.active = true;
        trialData.startedAt = new Date().toISOString();
        
        localStorage.setItem('freeTrialData', JSON.stringify(trialData));
        // Reset expired state flags when creating new trial
        localStorage.removeItem(FINAL_PROMPT_USED_KEY);
        localStorage.removeItem(TRIAL_EXPIRED_KEY);
        
        // Add to fingerprint history
        addToFingerprintHistory(trialData.fingerprint);
        
        console.log("FreeTrialManager: Incognito trial successfully initialized");
        
        // ADDED: Dispatch event with preserve flag
        const event = new CustomEvent('trialUpdated', { 
          detail: { 
            preserveThreadContent: true
          } 
        });
        window.dispatchEvent(event);
        
        clearInitLock();
        return trialData.active;
      }

      // 2. Check if Firebase is initialized properly
      if (!database) {
        console.error("FreeTrialManager: Firebase database not initialized");
        try {
          // Try to initialize Firebase again
          app = initializeApp(firebaseConfig);
          database = getDatabase(app);
          console.log("FreeTrialManager: Firebase initialized on retry");
        } catch (error) {
          console.error("FreeTrialManager: Firebase initialization error on retry:", error);
          // If Firebase still fails, create a local-only trial
          console.log("FreeTrialManager: Creating local-only trial due to Firebase failure");
          localStorage.setItem('freeTrialVerified', 'true');
          trialData.verified = true;
          
          // Assign a trial API key
          const randomIndex = Math.floor(Math.random() * ENCRYPTED_TRIAL_API_KEYS.length);
          trialData.trialKey = ENCRYPTED_TRIAL_API_KEYS[randomIndex];
          trialData.remaining = INITIAL_PROMPT_COUNT;
          trialData.active = true;
          trialData.startedAt = new Date().toISOString();
          trialData.isLocalOnlyTrial = true;
          
          localStorage.setItem('freeTrialData', JSON.stringify(trialData));
          
          // Add to fingerprint history
          addToFingerprintHistory(trialData.fingerprint);
          
          // Reset expired state flags when creating new trial
          localStorage.removeItem(FINAL_PROMPT_USED_KEY);
          localStorage.removeItem(TRIAL_EXPIRED_KEY);
          
          // ADDED: Dispatch event with preserve flag
          const event = new CustomEvent('trialUpdated', { 
            detail: { 
              preserveThreadContent: true
            } 
          });
          window.dispatchEvent(event);
          
          clearInitLock();
          return trialData.active;
        }
      }

      // 3. Check if user is blacklisted
      try {
        console.log("FreeTrialManager: Checking blacklist for fingerprint:", trialData.fingerprint);
        const blacklistRef = ref(database, 'blacklist');
        const blacklistQuery = query(blacklistRef, orderByChild('fingerprint'), equalTo(trialData.fingerprint));
        const blacklistSnapshot = await get(blacklistQuery);
        
        if (blacklistSnapshot.exists()) {
          console.log("FreeTrialManager: User is blacklisted");
          trialData.active = false;
          trialData.error = "User is blacklisted";
          localStorage.setItem('freeTrialData', JSON.stringify(trialData));
          clearInitLock();
          return false;
        }
        console.log("FreeTrialManager: Blacklist check passed");
      } catch (err) {
        console.error("FreeTrialManager: Blacklist check error:", err);
        // Continue even if blacklist check fails
      }

      // 4. Check if this fingerprint has already used a trial
      try {
        console.log("FreeTrialManager: Checking if trial already used");
        const usedTrialsRef = ref(database, 'usedTrials');
        const usedTrialsQuery = query(usedTrialsRef, orderByChild('fingerprint'), equalTo(trialData.fingerprint));
        const usedTrialsSnapshot = await get(usedTrialsQuery);
        
        if (usedTrialsSnapshot.exists() && !forceNew) {
          console.log("FreeTrialManager: Trial already used with this fingerprint");
          
          // Check if this is a returning user and try to restore their trial
          const existingTrials = [];
          usedTrialsSnapshot.forEach(childSnapshot => {
            existingTrials.push(childSnapshot.val());
          });
          
          if (existingTrials.length > 0) {
            console.log("FreeTrialManager: Found existing trial data in Firebase:", existingTrials[0]);
            
            // Add to fingerprint history
            addToFingerprintHistory(trialData.fingerprint);
            
            // Try to find associated usage records to determine remaining prompts
            try {
              const usageRef = ref(database, 'trialUsage');
              const usageQuery = query(usageRef, orderByChild('fingerprint'), equalTo(trialData.fingerprint));
              const usageSnapshot = await get(usageQuery);
              
              if (usageSnapshot.exists()) {
                // Count usage entries that counted against the trial
                let usedPrompts = 0;
                usageSnapshot.forEach(childSnapshot => {
                  const usageData = childSnapshot.val();
                  if (usageData.countedAgainstTrial) {
                    usedPrompts++;
                  }
                });
                
                // Calculate remaining prompts
                const remaining = Math.max(0, INITIAL_PROMPT_COUNT - usedPrompts);
                
                console.log(`FreeTrialManager: Found ${usedPrompts} used prompts, ${remaining} remaining`);
                
                // CRITICAL FIX: Check for final prompt usage pattern
                let usedFinalPrompt = false;
                if (remaining === 1) {
                  // Check if we have logs showing the final prompt was used
                  const expirationRef = ref(database, 'trialExpirations');
                  const expirationQuery = query(expirationRef, orderByChild('fingerprint'), equalTo(trialData.fingerprint));
                  const expirationSnapshot = await get(expirationQuery);
                  
                  if (expirationSnapshot.exists()) {
                    console.log("FreeTrialManager: Found expiration record, final prompt was used");
                    usedFinalPrompt = true;
                    // Save this to localStorage to persist across page refreshes
                    localStorage.setItem(FINAL_PROMPT_USED_KEY, 'true');
                    // Also mark as fully expired
                    markTrialAsExpired();
                  }
                }
                
                if (remaining > 0 && !usedFinalPrompt && localStorage.getItem(TRIAL_EXPIRED_KEY) !== 'true') {
                  // Restore the trial with remaining prompts
                  console.log("FreeTrialManager: Restoring trial with remaining prompts:", remaining);
                  trialData.active = true;
                  trialData.remaining = remaining;
                  trialData.trialKey = ENCRYPTED_TRIAL_API_KEYS[0]; // Use first key or match original if available
                  trialData.isRestoredTrial = true;
                  localStorage.setItem('freeTrialData', JSON.stringify(trialData));
                  
                  // ADDED: Dispatch event with preserve flag
                  const event = new CustomEvent('trialUpdated', { 
                    detail: { 
                      preserveThreadContent: true
                    } 
                  });
                  window.dispatchEvent(event);
                  
                  clearInitLock();
                  return true;
                } else {
                  console.log("FreeTrialManager: No remaining prompts for this user or final prompt was used");
                  trialData.active = false;
                  trialData.error = "Trial used up";
                  trialData.remaining = 0; // Ensure we show zero remaining
                  localStorage.setItem('freeTrialData', JSON.stringify(trialData));
                  // Also mark as fully expired
                  markTrialAsExpired();
                  clearInitLock();
                  return false;
                }
              }
            } catch (err) {
              console.error("FreeTrialManager: Error checking usage records:", err);
            }
          }
          
          // If we can't restore, treat as normal used trial
          trialData.active = false;
          trialData.error = "Trial already used";
          localStorage.setItem('freeTrialData', JSON.stringify(trialData));
          // Mark as fully expired
          markTrialAsExpired();
          clearInitLock();
          return false;
        }
        console.log("FreeTrialManager: Used trials check passed");
      } catch (err) {
        console.error("FreeTrialManager: Used trials check error:", err);
        // Continue even if check fails
      }

      // 5. Initialize new trial
      console.log("FreeTrialManager: Creating new trial");
      localStorage.setItem('freeTrialVerified', 'true');
      trialData.verified = true;
      
      // Assign a trial API key
      const randomIndex = Math.floor(Math.random() * ENCRYPTED_TRIAL_API_KEYS.length);
      trialData.trialKey = ENCRYPTED_TRIAL_API_KEYS[randomIndex];
      trialData.remaining = INITIAL_PROMPT_COUNT;
      trialData.active = true;
      trialData.startedAt = new Date().toISOString();
      
      // Always save to localStorage first to ensure local state is set
      localStorage.setItem('freeTrialData', JSON.stringify(trialData));
      
      // Add to fingerprint history
      addToFingerprintHistory(trialData.fingerprint);
      
      // Reset expired state flags when creating new trial
      localStorage.removeItem(FINAL_PROMPT_USED_KEY);
      localStorage.removeItem(TRIAL_EXPIRED_KEY);
      
      // Only log to Firebase if free trials are enabled
      if (FREE_TRIAL_ENABLED && database) {
        try {
          console.log("FreeTrialManager: Logging new trial to Firebase");
          // Log this fingerprint to used trials
          await push(ref(database, 'usedTrials'), {
            fingerprint: trialData.fingerprint,
            timestamp: new Date().toISOString(),
            trialKeyId: trialData.trialKey.id,
            deviceData: trialData.deviceData,
            referrer: document.referrer || 'direct',
            url: window.location.href
          });
          
          // Log trial initialization
          await push(ref(database, 'trialInitializations'), {
            fingerprint: trialData.fingerprint,
            timestamp: new Date().toISOString(),
            trialKeyId: trialData.trialKey.id,
            deviceData: trialData.deviceData,
            referrer: document.referrer || 'direct',
            url: window.location.href
          });
          
          console.log("FreeTrialManager: Successfully logged to Firebase");
        } catch (err) {
          console.error("FreeTrialManager: Failed to log trial initialization:", err);
          // Continue even if logging fails - local trial is already set up
        }
      }
      
      console.log("FreeTrialManager: Trial successfully initialized with key ID:", trialData.trialKey.id);
      
      // ADDED: Dispatch event with preserve flag
      const event = new CustomEvent('trialUpdated', { 
        detail: { 
          preserveThreadContent: true
        } 
      });
      window.dispatchEvent(event);
      
      clearInitLock();
      return trialData.active;
    } catch (error) {
      console.error("FreeTrialManager: Fatal error in initTrial:", error);
      
      // Create emergency fallback trial on fatal error
      try {
        console.log("FreeTrialManager: Creating emergency fallback trial");
        trialData = {
          fingerprint: 'emergency-' + Date.now(),
          verified: true,
          trialKey: ENCRYPTED_TRIAL_API_KEYS[0],
          remaining: INITIAL_PROMPT_COUNT,
          active: true,
          startedAt: new Date().toISOString(),
          isEmergencyTrial: true
        };
        localStorage.setItem('freeTrialData', JSON.stringify(trialData));
        // Reset expired state flags when creating emergency trial
        localStorage.removeItem(FINAL_PROMPT_USED_KEY);
        localStorage.removeItem(TRIAL_EXPIRED_KEY);
        
        // ADDED: Dispatch event with preserve flag
        const event = new CustomEvent('trialUpdated', { 
          detail: { 
            preserveThreadContent: true
          } 
        });
        window.dispatchEvent(event);
        
        clearInitLock();
        return true;
      } catch (e) {
        console.error("FreeTrialManager: Emergency fallback failed:", e);
        clearInitLock();
        return false;
      }
    }
  },

  isTrialActive() {
    // CRITICAL FIX: If in settings dialog context, we need to be extra careful
    if (inSettingsDialog) {
      console.log("FreeTrialManager: Checking trial active status within settings dialog");
      // While in settings, if trial was marked as expired, it should stay expired
      if (localStorage.getItem(TRIAL_EXPIRED_KEY) === 'true') {
        return false;
      }
    }
    
    // First check if trials are globally enabled
    if (!FREE_TRIAL_ENABLED) {
      return false;
    }
    
    // Check for any of the expiration indicators
    if (isTrialExpired()) {
      return false;
    }
    
    // Read directly from localStorage to ensure data is fresh
    try {
      const storedTrial = localStorage.getItem('freeTrialData');
      if (storedTrial) {
        const parsedData = JSON.parse(storedTrial);
        return parsedData.active && parsedData.remaining > 0;
      }
    } catch (err) {
      console.error("Error checking trial active status from localStorage:", err);
    }
    
    // Fall back to in-memory data
    return trialData && trialData.active && trialData.remaining > 0;
  },

  getTrialApiKey() {
    // If trial is marked as expired, never provide an API key
    if (isTrialExpired()) {
      return null;
    }
    
    // Refresh data from localStorage first
    try {
      const storedTrial = localStorage.getItem('freeTrialData');
      if (storedTrial) {
        const parsedData = JSON.parse(storedTrial);
        
        if (parsedData.active && parsedData.remaining > 0 && parsedData.trialKey) {
          trialData = parsedData;
        }
      }
    } catch (err) {
      console.error("Error refreshing trial data:", err);
    }
    
    if (!this.isTrialActive() || !trialData || !trialData.trialKey) return null;
    
    try {
      return decryptData(trialData.trialKey.key);
    } catch (err) {
      console.error("Error decrypting trial API key:", err);
      return null;
    }
  },

  getRemainingPromptCount() {
    // If trial is marked as expired, always return 0
    if (isTrialExpired()) {
      return 0;
    }
    
    // Refresh data from localStorage first
    try {
      const storedTrial = localStorage.getItem('freeTrialData');
      if (storedTrial) {
        const parsedData = JSON.parse(storedTrial);
        if (parsedData && parsedData.remaining !== undefined) {
          return parsedData.remaining;
        }
      }
    } catch (err) {
      console.error("Error reading trial data from localStorage:", err);
    }
    
    // Fall back to in-memory data
    return trialData && trialData.remaining ? trialData.remaining : 0;
  },
  
  // Check if remaining resources trigger warning
  checkResourceWarning() {
    // If trial is marked as expired, don't show warnings
    if (isTrialExpired()) {
      return false;
    }
    
    // Get fresh data
    let remaining = this.getRemainingPromptCount();
    if (remaining <= 0) return false;
    
    // Calculate if remaining prompts are below warning threshold
    const remainingPercentage = remaining / INITIAL_PROMPT_COUNT;
    const shouldWarn = remainingPercentage <= WARNING_THRESHOLD;
    
    // If we're at the warning threshold and haven't shown warning yet
    if (shouldWarn && !hasShownWarning) {
      hasShownWarning = true;
      
      // Dispatch custom event for resource warning
      const event = new CustomEvent('trialResourceWarning', { 
        detail: { 
          remaining: remaining,
          total: INITIAL_PROMPT_COUNT,
          percentage: remainingPercentage 
        } 
      });
      window.dispatchEvent(event);
      
      console.log(`FreeTrialManager: Resource warning triggered at ${Math.round(remainingPercentage * 100)}% remaining`);
      return true;
    }
    
    return false;
  },

  async recordSuccessfulPrompt(data = {}, countAgainstTrial = false) {
    console.log("FreeTrialManager: Recording prompt usage, countAgainstTrial:", countAgainstTrial);
    
    // Store fingerprint and trial key ID before checking expired status
    // This ensures we can still log the final prompt usage to Firebase
    const currentFingerprint = trialData ? trialData.fingerprint : null;
    const currentTrialKeyId = trialData && trialData.trialKey ? trialData.trialKey.id : 'unknown';
    const currentDeviceData = trialData ? trialData.deviceData : null;
    const startedAt = trialData ? trialData.startedAt : null;
    
    // Get initial trial state for tracking changes
    const initialRemaining = this.getRemainingPromptCount();
    const wasActive = this.isTrialActive();
    
    // ADDED: Capture if this might be final prompt
    const isFinalPrompt = initialRemaining === 1 && countAgainstTrial;
    
    // If trial is already marked as expired, we still want to log the final usage
    // but we'll check that below
    let isAlreadyExpired = isTrialExpired();
    
    // Reload trial data from localStorage to ensure it's up to date
    if (!isAlreadyExpired) {
      try {
        const storedTrial = localStorage.getItem('freeTrialData');
        if (storedTrial) {
          trialData = JSON.parse(storedTrial);
        }
      } catch (err) {
        console.error("Error refreshing trial data before recording usage:", err);
      }
    }
    
    // Only decrement remaining prompts if countAgainstTrial is true 
    // and trial not already expired
    if (countAgainstTrial && !isAlreadyExpired && wasActive) {
      console.log(`FreeTrialManager: Decrementing remaining count from ${trialData.remaining} to ${trialData.remaining - 1}`);
      
      trialData.remaining -= 1;
      
      // Update localStorage with new remaining count
      localStorage.setItem('freeTrialData', JSON.stringify(trialData));
      
      // Check if we should show warning after decrementing
      this.checkResourceWarning();
      
      // If this was the final prompt, mark it in localStorage to persist across refreshes
      if (isFinalPrompt) {
        console.log("FreeTrialManager: Final prompt used, setting permanent flag");
        localStorage.setItem(FINAL_PROMPT_USED_KEY, 'true');
      }
      
      // Mark trial as inactive if we've reached 0 remaining prompts
      if (trialData.remaining === 0) {
        console.log("FreeTrialManager: Trial has exactly 0 prompts remaining, marking as expired");
        trialData.active = false;
        trialData.expiredAt = new Date().toISOString();
        localStorage.setItem('freeTrialData', JSON.stringify(trialData));
        
        // Also mark as fully expired
        markTrialAsExpired();
        
        // Log trial expiration if trials are enabled
        if (FREE_TRIAL_ENABLED && database) {
          try {
            await push(ref(database, 'trialExpirations'), {
              fingerprint: currentFingerprint,
              timestamp: new Date().toISOString(),
              trialKeyId: currentTrialKeyId,
              startedAt: startedAt,
              deviceData: currentDeviceData
            });
            console.log("FreeTrialManager: Successfully logged trial expiration");
          } catch (e) {
            console.error("FreeTrialManager: Failed to log trial expiration:", e);
          }
        }
      }
    }

    // ADDED: Always log usage to Firebase for the final prompt
    // even if the trial is expired or inactive
    if (FREE_TRIAL_ENABLED && database && (wasActive || isFinalPrompt)) {
      // Log usage to Firebase
      try {
        console.log("FreeTrialManager: Logging usage to Firebase");
        await push(ref(database, 'trialUsage'), {
          fingerprint: currentFingerprint,
          timestamp: new Date().toISOString(),
          remaining: trialData ? trialData.remaining : 0,
          trialKeyId: currentTrialKeyId,
          deviceData: currentDeviceData,
          url: window.location.href,
          referrer: document.referrer || 'direct',
          countedAgainstTrial: countAgainstTrial, // Include flag to track which calls count
          isFinalPrompt: isFinalPrompt
        });
        console.log("FreeTrialManager: Successfully logged usage to Firebase");
      } catch (e) {
        console.error("FreeTrialManager: Firebase logging failed:", e);
      }
    }
    
    // ADDED: Dispatch event with preserve flag
    const event = new CustomEvent('trialUpdated', { 
      detail: { 
        preserveThreadContent: true,
        isFinalPrompt: isFinalPrompt
      } 
    });
    window.dispatchEvent(event);
    
    return trialData ? trialData.remaining : 0;
  },
  
  // Helper method to check if user is in blacklist (for admin use)
  async checkBlacklist(fingerprint) {
    if (!FREE_TRIAL_ENABLED || !database) return false;
    
    try {
      const blacklistRef = ref(database, 'blacklist');
      const blacklistQuery = query(blacklistRef, orderByChild('fingerprint'), equalTo(fingerprint || (trialData ? trialData.fingerprint : '')));
      const snapshot = await get(blacklistQuery);
      return snapshot.exists();
    } catch (err) {
      console.error("Blacklist check error:", err);
      return false;
    }
  },
  
  // MODIFIED: Only allow trial reset in test environments
  async resetTrialForTesting() {
    if (!isTestEnvironment) {
      console.error("resetTrialForTesting can only be used in development/test mode");
      return false;
    }
    
    localStorage.removeItem('freeTrialData');
    localStorage.removeItem('freeTrialVerified');
    localStorage.removeItem(FINAL_PROMPT_USED_KEY); // Reset the final prompt flag
    localStorage.removeItem(TRIAL_EXPIRED_KEY); // Reset the expired flag
    localStorage.removeItem(TRIAL_INIT_LOCK_KEY); // Reset the init lock
    localStorage.removeItem(TRIAL_INIT_TIMESTAMP_KEY); // Reset the init timestamp
    
    trialData = null;
    hasShownWarning = false; // Reset warning flag
    initializationAttempts = 0; // Reset attempts counter
    inSettingsDialog = false; // Reset settings dialog state
    
    // For testing, also remove from usedTrials in Firebase
    if (FREE_TRIAL_ENABLED && database && trialData && trialData.fingerprint) {
      try {
        const usedTrialsRef = ref(database, 'usedTrials');
        const usedTrialsQuery = query(usedTrialsRef, orderByChild('fingerprint'), equalTo(trialData.fingerprint));
        const snapshot = await get(usedTrialsQuery);
        
        if (snapshot.exists()) {
          // This would normally require admin rights
          console.log("In a real app, would remove from usedTrials:", trialData.fingerprint);
        }
      } catch (err) {
        console.error("Error in resetTrialForTesting:", err);
      }
    }
    
    console.log("Trial reset for testing");
    return true;
  },
  
  // Function to manually create a trial (for testing)
  forceCreateTrial(remainingCount = INITIAL_PROMPT_COUNT) {
    // Only allow this in test environments
    if (!isTestEnvironment) {
      console.error("forceCreateTrial can only be used in development/test mode");
      return false;
    }
    
    console.log("FreeTrialManager: Force creating a trial with", remainingCount, "prompts");
    const randomFingerprint = 'forced-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15);
    trialData = {
      fingerprint: randomFingerprint,
      verified: true,
      trialKey: ENCRYPTED_TRIAL_API_KEYS[0],
      remaining: remainingCount,
      active: true,
      startedAt: new Date().toISOString(),
      isForcedTrial: true,
      deviceData: {
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
        forcedCreation: true
      }
    };
    
    localStorage.setItem('freeTrialData', JSON.stringify(trialData));
    localStorage.setItem('freeTrialVerified', 'true');
    // Reset all state flags
    localStorage.removeItem(FINAL_PROMPT_USED_KEY);
    localStorage.removeItem(TRIAL_EXPIRED_KEY);
    localStorage.removeItem(TRIAL_INIT_LOCK_KEY);
    localStorage.removeItem(TRIAL_INIT_TIMESTAMP_KEY);
    
    hasShownWarning = false; // Reset warning flag
    initializationAttempts = 0; // Reset attempts counter
    inSettingsDialog = false; // Reset settings dialog state
    
    // ADDED: Dispatch event with preserve flag
    const event = new CustomEvent('trialUpdated', { 
      detail: { 
        preserveThreadContent: true
      } 
    });
    window.dispatchEvent(event);
    
    console.log("FreeTrialManager: Force created trial with key ID:", trialData.trialKey.id);
    
    return true;
  },
  
  // Global function to disable all free trials
  disableTrials() {
    // Modify the global free trial status flag 
    // Note: This would normally be done via an admin API instead of directly
    // This is just a demonstration
    localStorage.setItem('free_trial_enabled', 'false');
    
    // Also update current trial data
    if (trialData) {
      trialData.active = false;
      trialData.error = "Free trials have been disabled";
      localStorage.setItem('freeTrialData', JSON.stringify(trialData));
    }
    
    console.log("Free trials have been disabled");
    
    // ADDED: Dispatch event with preserve flag
    const event = new CustomEvent('trialUpdated', { 
      detail: { 
        preserveThreadContent: true
      } 
    });
    window.dispatchEvent(event);
    
    return true;
  },
  
  // Debugging function to directly check trial data
  getTrialData() {
    // Always get the latest data from localStorage
    let storedData = null;
    try {
      storedData = localStorage.getItem('freeTrialData');
      if (storedData) {
        trialData = JSON.parse(storedData);
      }
    } catch (err) {
      console.error("Error parsing trial data for debug:", err);
    }
    
    // Get the trial state flags
    const finalPromptUsed = localStorage.getItem(FINAL_PROMPT_USED_KEY) === 'true';
    const trialExpired = localStorage.getItem(TRIAL_EXPIRED_KEY) === 'true';
    const initLock = localStorage.getItem(TRIAL_INIT_LOCK_KEY) === 'true';
    const initTimestamp = localStorage.getItem(TRIAL_INIT_TIMESTAMP_KEY);
    
    // Get fingerprint history
    let fingerprintHistory = [];
    try {
      const historyString = localStorage.getItem(FINGERPRINT_HISTORY_KEY);
      if (historyString) {
        fingerprintHistory = JSON.parse(historyString);
      }
    } catch (err) {
      console.error("Error parsing fingerprint history:", err);
    }
    
    return {
      trialData,
      storedData,
      isIncognitoMode,
      FREE_TRIAL_ENABLED,
      hasShownWarning,
      initializationAttempts,
      finalPromptUsed,
      trialExpired,
      initLock,
      initTimestamp,
      inSettingsDialog,
      isTestEnvironment,
      fingerprintHistory
    };
  },
  
  // Function to force clear the trial state flags (for debugging)
  resetTrialStateFlags() {
    // Only allow this in test environments
    if (!isTestEnvironment) {
      console.error("resetTrialStateFlags can only be used in development/test mode");
      return false;
    }
    
    localStorage.removeItem(FINAL_PROMPT_USED_KEY);
    localStorage.removeItem(TRIAL_EXPIRED_KEY);
    localStorage.removeItem(TRIAL_INIT_LOCK_KEY);
    localStorage.removeItem(TRIAL_INIT_TIMESTAMP_KEY);
    console.log("FreeTrialManager: Reset all trial state flags");
    return true;
  },
  
  // Specifically for authorized testing in development/test environments
  enableTestMode() {
    isTestEnvironment = true;
    console.log("FreeTrialManager: Test mode enabled - some restrictions lifted for testing");
    return true;
  }
};

export default FreeTrialManager;